import React, { useEffect, useState } from "react";
import { Zap, LayoutDashboard, CheckSquare, Users, Settings, LogOut, Bell, ChevronDown, ChevronRight, Plus, MessageSquare, Star, Archive, BarChart2, Building2, TrendingUp, Trash2, Package, CalendarDays, UserCheck, ShoppingCart, Clock, Wrench, Shield, Receipt, FileText, SettingsIcon, Search, Megaphone, RotateCcw, Tags, Calculator, Truck, Shuffle, Timer } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { collection, doc, onSnapshot, query, where, orderBy, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { sezioniUtente } from "../utils/permessiEffettivi";
import SearchBar from "./SearchBar";
import { usePiano } from "../utils/usePiano";
import GlobalSearch from "./GlobalSearch";
import DeleteProjectModal from "./DeleteProjectModal";
import { PAGINA_MODULO, MODULI, PIANI_INFO, statoModulo } from "../utils/pianiNexiora";
import { commessaVisibile } from "../utils/condivisione";
import { urlGuida, EVENTO_SCHEDA_COMMESSA } from "../utils/guidaOnline";

function SidebarGroup({ id, label, defaultOpen=true, children, viola=false, barra=false, pagine=[], currentPage=null }) {
  const [open, setOpen] = React.useState(() => {
    try { const s = localStorage.getItem(`sidebar_group_${id}`); return s !== null ? s==="true" : defaultOpen; } catch { return defaultOpen; }
  });
  // Se la pagina in cui ti trovi sta dentro questo gruppo, il gruppo si apre da solo —
  // altrimenti arrivandoci da una scorciatoia o da un link ti troveresti la voce attiva
  // nascosta dentro un gruppo chiuso, senza capire dove sei. Resta comunque richiudibile
  // a mano subito dopo: si apre al cambio pagina, non a ogni ridisegno.
  const paginaPrec = React.useRef(currentPage);
  React.useEffect(() => {
    if (paginaPrec.current === currentPage) return;
    paginaPrec.current = currentPage;
    if (currentPage && pagine.includes(currentPage)) setOpen(true);
  }, [currentPage, pagine]);
  const toggle = () => {
    const nuovoStato = !open;
    setOpen(nuovoStato);
    try { localStorage.setItem(`sidebar_group_${id}`, String(nuovoStato)); } catch {}
  };
  // W444: un gruppo senza voci visibili (per i permessi di chi è collegato) non si mostra
  if (React.Children.toArray(children).length === 0) return null;
  return (
    <div style={{marginBottom:2}} data-gruppo={label}>
      <div onClick={toggle} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px",cursor:"pointer",userSelect:"none",borderRadius:viola?6:0,margin:viola?"2px 4px":0,background:viola?"rgba(124,58,237,0.15)":"transparent"}}
        onMouseOver={e=>e.currentTarget.style.background=viola?"rgba(124,58,237,0.25)":"rgba(255,255,255,0.05)"}
        onMouseOut={e=>e.currentTarget.style.background=viola?"rgba(124,58,237,0.15)":"transparent"}>
        <span style={{fontSize:10,fontWeight:700,color:viola?"#c4b5fd":"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</span>
        <span style={{fontSize:10,color:viola?"#c4b5fd":"rgba(255,255,255,0.3)",transition:"transform 0.2s",display:"inline-block",transform:open?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
      </div>
      {/* barra: sottile linea verticale viola + leggero rientro, per far percepire i figli come "contenuti"
          nel gruppo anche quando lo sfondo dell'header (viola) non è più a vista scorrendo la lista. */}
      {open && (
        <div style={barra ? {borderLeft:"2px solid rgba(124,58,237,0.45)", marginLeft:14, paddingLeft:4} : undefined}>
          {children}
        </div>
      )}
    </div>
  );
}

// Definito FUORI dal componente Sidebar e memoizzato: se restasse dentro, React lo
// tratterebbe come un componente nuovo a ogni cambio pagina e ricostruirebbe da zero
// tutte le voci del menu — perdendo la posizione della barra di scorrimento, che è
// esattamente il salto fastidioso per cui bisognava riscorrere ogni volta per tornare
// dove si aveva cliccato.
// Stato del piano per le voci della barra laterale (un solo ascolto, in Sidebar).
const PianoNavContext = React.createContext(null);

const NavItem = React.memo(function NavItem({ id, icon: Icon, label, count, currentPage, currentProjectId, onNavigate }) {
  const statoPianoNav = React.useContext(PianoNavContext);
  const modulo = PAGINA_MODULO[id];
  const inSolaLettura = modulo && statoModulo(modulo, statoPianoNav) === "readonly";
  return (
    <button
      data-nav={id}
      className={`nav-item ${currentPage === id && !currentProjectId ? "active" : ""}`}
      onClick={() => onNavigate({ page: id })}
      title={inSolaLettura ? `Sola lettura — modulo del piano ${PIANI_INFO[MODULI[modulo].piano].label}` : undefined}>
      <Icon size={16}/> {label}
      {inSolaLettura && <span style={{ marginLeft:"auto", fontSize:9.5, fontWeight:700, padding:"1px 6px", borderRadius:6, background:"rgba(217,119,6,0.18)", color:"#d97706" }}>🔒 {PIANI_INFO[MODULI[modulo].piano].label}</span>}
      {count > 0 && <span className="count">{count}</span>}
    </button>
  );
});

export default function Sidebar({ currentPage, currentProjectId, onNavigate, notifCount, onBackToOwner, projectHealthMap = {}, vediLeMieOre = false }) {
  const { userProfile, isOrgAdmin, isTitolare, isPlatformOwner, logout, currentUser } = useAuth();
  // Il PM fa le richieste di fatturazione e vede le sue (Richieste di fatturazione)
  const isPm = userProfile?.ruoloOperativo === "pm";
  // «Inizia da qui» accanto a Home nelle prime 2 settimane dal primo ingresso dell'utente
  // (stesso periodo del messaggio di benvenuto in Home, components/BenvenutoNuovoUtente.js)
  const primoIngresso = userProfile?.createdAt?.toDate ? userProfile.createdAt.toDate() : null;
  const nuovoArrivato = !isPlatformOwner && !!primoIngresso && (Date.now() - primoIngresso.getTime()) < 14 * 86400000;
  const { effectiveOrgId } = useOrg();
  const { hasFeature } = useFeatures();
  const [projects, setProjects] = useState([]);
  // Organizzazione DAVVERO in uso — "organization" da useAuth() è sempre il profilo
  // personale di chi è loggato, non cambia mai con "Entra". Bug reale trovato oggi, e più
  // serio di quanto sembrasse: non solo la scritta "per..." era sbagliata, anche il
  // controllo che decide se mostrare "Parco Mezzi" guardava l'organizzazione sbagliata —
  // un Platform Owner in visita a un'altra organizzazione vedeva (o non vedeva) quella voce
  // in base alle impostazioni della PROPRIA organizzazione, non di quella che stava usando.
  const [orgVisitata, setOrgVisitata] = useState(null);
  useEffect(() => {
    if (!effectiveOrgId) { setOrgVisitata(null); return; }
    return onSnapshot(doc(db, "organizations", effectiveOrgId), snap => {
      setOrgVisitata(snap.exists() ? snap.data() : null);
    });
  }, [effectiveOrgId]);
  const [clienti, setClienti] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [favOpen, setFavOpen] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [clientiAperti, setClientiAperti] = useState({});
  const toggleCliente = id => setClientiAperti(s=>({...s,[id]:!s[id]}));
  const [taskCountMap, setTaskCountMap] = useState({});
  const [rcMieAperteCount, setRcMieAperteCount] = useState(0);
  const [ddtIngrDaVerificareCount, setDdtIngrDaVerificareCount] = useState(0);
  const [segMieAperteCount, setSegMieAperteCount] = useState(0);

  // Badge: segnalazioni cliente assegnate a me e ancora aperte
  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    return onSnapshot(
      // solo le mie (filtro nel database: prima arrivavano tutte quelle dell'azienda)
      query(collection(db,"segnalazioniCliente"), where("orgId","==",effectiveOrgId), where("assegnatarioUid","==",currentUser.uid)),
      snap => setSegMieAperteCount(snap.docs.filter(d=>d.data().stato!=="chiusa").length)
    );
  }, [effectiveOrgId, currentUser]);

  // Badge: richieste commerciali assegnate a me e ancora aperte
  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    return onSnapshot(
      // solo le mie (filtro nel database: prima arrivavano tutte quelle dell'azienda)
      query(collection(db,"richiesteCommerciali"), where("orgId","==",effectiveOrgId), where("assegnatarioUid","==",currentUser.uid)),
      snap => setRcMieAperteCount(snap.docs.filter(d=>d.data().stato!=="annullata").length)
    );
  }, [effectiveOrgId, currentUser]);

  // Badge: DDT in ingresso ancora da verificare
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      // solo quelli da verificare (filtro nel database: prima arrivavano tutti i DDT dell'azienda)
      query(collection(db,"ddtIngresso"), where("orgId","==",effectiveOrgId), where("stato","==","ricevuto")),
      snap => setDdtIngrDaVerificareCount(snap.size)
    );
  }, [effectiveOrgId]);

  React.useEffect(() => {
    if (!effectiveOrgId) return;
    // Un solo where (organizationId) — niente indice composito necessario. Il filtro sullo
    // stato "completata" si fa lato client, come già altrove in Nexiora per questo stesso motivo.
    const q = query(
      collection(db, "tasks"),
      where("organizationId", "==", effectiveOrgId)
    );
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const t = d.data();
        if (t.status !== "completata" && t.projectId) map[t.projectId] = (map[t.projectId] || 0) + 1;
      });
      setTaskCountMap(map);
    });
  }, [effectiveOrgId]);
  const canDelete = isOrgAdmin || isPlatformOwner;

  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    const q = query(
      collection(db, "projects"),
      where("organizationId", "==", effectiveOrgId)
    );
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => showArchived ? p.archived : !p.archived);
      list.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setProjects(list);
    });
  }, [effectiveOrgId, showArchived, currentUser]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","it"))));
  }, [effectiveOrgId]);

  // Carica preferiti utente
  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    // Un solo where (userId) — niente indice composito necessario. I preferiti di un utente
    // sono comunque pochi, il filtro per organizzazione lato client non pesa.
    const q = query(
      collection(db, "userFavorites"),
      where("userId", "==", currentUser.uid)
    );
    return onSnapshot(q, snap => {
      setFavorites(snap.docs.filter(d => d.data().organizationId === effectiveOrgId).map(d => d.data().projectId));
    });
  }, [effectiveOrgId, currentUser]);

  const toggleFavorite = async (e, project) => {
    e.stopPropagation();
    const { getDocs, addDoc, deleteDoc, doc: fDoc, collection: fCol, query: fQuery, where: fWhere } = await import("firebase/firestore");
    const { db: fdb } = await import("../firebase");
    const snap = await getDocs(fQuery(fCol(fdb, "userFavorites"),
      fWhere("userId", "==", currentUser.uid),
      fWhere("projectId", "==", project.id)
    ));
    if (snap.empty) {
      await addDoc(fCol(fdb, "userFavorites"), {
        userId: currentUser.uid,
        projectId: project.id,
        organizationId: effectiveOrgId,
        createdAt: new Date()
      });
    } else {
      await deleteDoc(fDoc(fdb, "userFavorites", snap.docs[0].id));
    }
  };

  const [soloMieCommesse, setSoloMieCommesse] = useState(false);
  // Scheda attiva della commessa aperta: serve al «?» per aprire la guida giusta
  const [schedaCommessa, setSchedaCommessa] = useState(null);
  useEffect(() => {
    const h = e => setSchedaCommessa(e.detail || null);
    window.addEventListener(EVENTO_SCHEDA_COMMESSA, h);
    return () => window.removeEventListener(EVENTO_SCHEDA_COMMESSA, h);
  }, []);
  const isFavorite = id => favorites.includes(id);
  const favoriteProjects = projects.filter(p => isFavorite(p.id));
  const regularProjects = projects.filter(p => {
    if(!isFavorite(p.id)) {
      // Filtro visibilità — se il progetto ha membri, mostra solo a chi ne fa parte (+ admin/owner)
      // Visibilità: membri della commessa o persone con cui è condivisa (utils/condivisione.js)
      if(!commessaVisibile(p,{ uid: currentUser?.uid, isAdmin: isOrgAdmin||isPlatformOwner })) return false;
      if(soloMieCommesse && p.responsabileUid !== currentUser?.uid) return false;
      return true;
    }
    return false;
  });

  // Abilitazioni sezioni — stesso modello di useCanSee: base per ruolo (con default sensati
  // se non configurato) più aggiunte/rimozioni per singola persona, mai sostituzione totale.
  const [sezioniAbilitate, setSezioniAbilitate] = React.useState(null);
  React.useEffect(() => {
    if (!effectiveOrgId || isPlatformOwner || isOrgAdmin) return;
    return onSnapshot(doc(db, "abilitazioniSezioni", effectiveOrgId), snap => {
      const data = snap.exists() ? snap.data() : {};
      setSezioniAbilitate(sezioniUtente(data, { ruolo: userProfile?.ruoloOperativo, uid: currentUser?.uid }));
    });
  }, [effectiveOrgId, isPlatformOwner, isOrgAdmin, userProfile, currentUser]);
  // Colore stato commessa: usa projectHealthMap se disponibile, altrimenti colore utente
  const getHealthDot = (p) => {
    const health = projectHealthMap[p.id];
    if (health === "critical") return "#dc2626";
    if (health === "warning")  return "#d97706";
    if (health === "ok" && projectHealthMap[p.id]) return "#059669";
    return p.color || "#7c3aed"; // colore utente se non ci sono task
  };

  const canSee = (sezId) => {
    if (isPlatformOwner || isOrgAdmin) return true;
    if (sezioniAbilitate === null) return true;
    return sezioniAbilitate.has(sezId);
  };

  // Impostazioni Aziendali — unica eccezione al comportamento generale sopra: un admin non
  // titolare (es. un PM con permessi ampi) non la vede di default, a differenza di tutte le
  // altre sezioni. La vede solo se esplicitamente abilitato per lui in Abilitazioni — stesso
  // meccanismo già esistente, qui applicato anche a un ruolo admin (che normalmente salta
  // questo controllo, dato che vede già tutto il resto).
  const [impostazioniOrgAbilitataPerAdmin, setImpostazioniOrgAbilitataPerAdmin] = React.useState(false);
  React.useEffect(() => {
    if (!effectiveOrgId || isPlatformOwner || isTitolare || !isOrgAdmin) { setImpostazioniOrgAbilitataPerAdmin(false); return; }
    return onSnapshot(doc(db, "abilitazioniSezioni", effectiveOrgId), snap => {
      const custom = snap.exists() ? snap.data().utenti?.[currentUser?.uid] : null;
      setImpostazioniOrgAbilitataPerAdmin(!!custom?.includes("impostazioni-org"));
    });
  }, [effectiveOrgId, isPlatformOwner, isTitolare, isOrgAdmin, currentUser]);
  const canSeeImpostazioniOrg = isTitolare || isPlatformOwner || impostazioniOrgAbilitataPerAdmin;

  const [parcoMezziAbilitatoPerAdmin, setParcoMezziAbilitatoPerAdmin] = React.useState(false);
  React.useEffect(() => {
    if (!effectiveOrgId || isPlatformOwner || isTitolare || !isOrgAdmin) { setParcoMezziAbilitatoPerAdmin(false); return; }
    return onSnapshot(doc(db, "abilitazioniSezioni", effectiveOrgId), snap => {
      const custom = snap.exists() ? snap.data().utenti?.[currentUser?.uid] : null;
      setParcoMezziAbilitatoPerAdmin(!!custom?.includes("gestione-parco-mezzi"));
    });
  }, [effectiveOrgId, isPlatformOwner, isTitolare, isOrgAdmin, currentUser]);
  const canSeeParcoMezzi = isTitolare || isPlatformOwner || parcoMezziAbilitatoPerAdmin;
  const canSeeTab = (tabId) => {
    if (isPlatformOwner || isOrgAdmin) return true;
    if (sezioniAbilitate === null) return true; // default: tutti i tab visibili
    // I tab hanno id che iniziano con "tab-"
    // Se sezioniAbilitate non contiene NESSUN tab configurato (es. utente con solo ruolo),
    // mostriamo tutti i tab per default — i tab vengono gestiti solo con override individuale
    const tabIds = ["tab-tutto","tab-progetti","tab-personale","tab-acquisti","tab-gestione"];
    const haTabConfig = tabIds.some(t => sezioniAbilitate.has(t));
    if (!haTabConfig) return true; // nessun tab configurato = tutti visibili
    return sezioniAbilitate.has(tabId);
  };

  const [showGlobalSearch, setShowGlobalSearch] = React.useState(false);
  const { piano, giorniRimasti, isTrial, isBase, stato: statoPianoNav } = usePiano(effectiveOrgId);
  const [sidebarTab, setSidebarTab] = React.useState(isPlatformOwner||isOrgAdmin?"tutto":"progetti");

  // Shortcut tastiera: Cmd+K / Ctrl+K per aprire ricerca
  React.useEffect(()=>{
    const handler = e => { if((e.metaKey||e.ctrlKey)&&e.key==="k"){ e.preventDefault(); setShowGlobalSearch(true); } };
    window.addEventListener("keydown",handler);
    return ()=>window.removeEventListener("keydown",handler);
  },[]);


  // ═══ W444: barra laterale riorganizzata (decisa con Paolo il 30/10/2026) ═══
  // Gestione = anagrafiche (nell'ordine in cui si compilano all'avvio) + approvazioni (le code di
  // lavoro dell'amministrazione, compresa la richiesta di fatturazione del PM); poi Pratiche e
  // documenti, Registri (solo archivio) e Configurazione. Ogni voce tiene esattamente la condizione
  // di visibilità di prima: cambia solo il gruppo in cui sta (prova: demo/tour/menu_voci.mjs).
  const anagrafiche = [
    (isOrgAdmin||isPlatformOwner) && <NavItem key="users" id="users" icon={Users} label="Gestione Utenti" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("dipendenti") && <NavItem key="dipendenti" id="dipendenti" icon={UserCheck} label="Dipendenti" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("clienti") && <NavItem key="clienti" id="clienti" icon={Building2} label="Clienti" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("ditte-esterne") && <NavItem key="ditte-esterne" id="ditte-esterne" icon={Truck} label="Ditte esterne" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("gestione-contratti-manutenzione") && <NavItem key="gcm" id="gestione-contratti-manutenzione" icon={Wrench} label="Contratti di Manutenzione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    orgVisitata?.disponibilitaMezziAbilitato && canSeeParcoMezzi && <NavItem key="parco-mezzi" id="parco-mezzi" icon={Truck} label="Parco Mezzi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    orgVisitata?.impiantiVerificheAttivi && canSee("impianti") && <NavItem key="impianti" id="impianti" icon={Zap} label="Anagrafica impianti" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
  ].filter(Boolean);
  const approvazioni = [
    canSee("approvazione-cartellini") && <NavItem key="ac" id="approvazione-cartellini" icon={CheckSquare} label="Approvazione Cartellini" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("approvazione-note-spese") && <NavItem key="ans" id="approvazione-note-spese" icon={Receipt} label="Approvazione Note Spese" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    canSee("approvazione-ferie-permessi") && <NavItem key="afp" id="approvazione-ferie-permessi" icon={CalendarDays} label="Approvazione Ferie e Permessi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
    (canSee("da-fatturare")||isPm) && <NavItem key="df" id="da-fatturare" icon={FileText} label="Richieste di fatturazione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>,
  ].filter(Boolean);
  const sottotitolo = (t) => <div key={"st-"+t} style={{fontSize:9.5,fontWeight:700,color:"rgba(255,255,255,0.32)",textTransform:"uppercase",letterSpacing:"0.08em",padding:"6px 12px 2px"}}>{t}</div>;
  const gruppiAmministrazione = (<>
    <SidebarGroup id="gestione" pagine={["users","dipendenti","clienti","ditte-esterne","gestione-contratti-manutenzione","parco-mezzi","impianti","approvazione-cartellini","approvazione-note-spese","approvazione-ferie-permessi","da-fatturare"]} currentPage={currentPage} label="Gestione" defaultOpen={false} viola={true}>
      {anagrafiche.length > 0 && approvazioni.length > 0 && sottotitolo("Anagrafiche")}
      {anagrafiche}
      {anagrafiche.length > 0 && approvazioni.length > 0 && sottotitolo("Approvazioni")}
      {approvazioni}
    </SidebarGroup>
    <SidebarGroup id="pratiche-documenti" pagine={["pratiche-aperte","documenti-subappaltatori"]} currentPage={currentPage} label="Pratiche e documenti" defaultOpen={false} viola={true}>
      {canSee("pratiche") && (<NavItem id="pratiche-aperte" icon={FileText} label="Pratiche aperte" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {(canSee("documenti-subappaltatori")||canSee("documenti-subappaltatori-verifica")) && (<NavItem id="documenti-subappaltatori" icon={Shield} label="Documenti subappaltatori" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
    </SidebarGroup>
    <SidebarGroup id="registri" pagine={["elaborazione-offerte","registro-cavi","registro-commesse","registro-corrispondenza","registro-ddt","registro-ddt-ingresso","registro-interventi","registro-noleggi","registro-rapportini","registro-richieste-comm","registro-rientri-materiali","registro-sal","registro-segnalazioni","registro-sopralluoghi","verbali"]} currentPage={currentPage} label="📚 Registri" defaultOpen={false} viola={true}>
      {canSee("clienti") && (<NavItem id="registro-commesse" icon={FileText} label="Registro Commesse" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {canSee("registro-sal") && (<NavItem id="registro-sal" icon={FileText} label="Registro SAL" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {canSee("elaborazione-offerte") && (<NavItem id="elaborazione-offerte" icon={Calculator} label="Registro Offerte" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {canSee("registro-rapportini") && (<NavItem id="registro-rapportini" icon={FileText} label="Registro rapportini" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {canSee("registro-interventi") && (<NavItem id="registro-interventi" icon={FileText} label="Registro interventi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      <NavItem id="registro-sopralluoghi" icon={FileText} label="Registro sopralluoghi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-richieste-comm" icon={FileText} label="Registro Richieste Commerciali" count={rcMieAperteCount||0} currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-segnalazioni" icon={Megaphone} label="Registro Segnalazioni Cliente" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-ddt" icon={FileText} label="Registro DDT in uscita" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-ddt-ingresso" icon={FileText} label="Registro DDT in ingresso" count={ddtIngrDaVerificareCount||0} currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-corrispondenza" icon={FileText} label="Registro corrispondenza" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-rientri-materiali" icon={RotateCcw} label="Registro Rientri Materiali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      <NavItem id="registro-cavi" icon={Shuffle} label="Registro Cavi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
      {canSee("registro-noleggi") && (<NavItem id="registro-noleggi" icon={FileText} label="Registro e gestione noleggi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      <NavItem id="verbali" icon={FileText} label="Verbali riunione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
    </SidebarGroup>
    <SidebarGroup id="configurazione" pagine={["abilitazioni","impostazioni-org"]} currentPage={currentPage} label="Configurazione" defaultOpen={false} viola={true}>
      {(isOrgAdmin||isPlatformOwner||canSee("gestione-tab-visibilita")) && (<NavItem id="abilitazioni" icon={Shield} label="Abilitazioni" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
      {canSeeImpostazioniOrg && (<NavItem id="impostazioni-org" icon={SettingsIcon} label="Impostazioni aziendali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
    </SidebarGroup>
  </>);

  return (
    <PianoNavContext.Provider value={statoPianoNav}>
    <div className="sidebar">
      <div className="sidebar-logo">
        <svg width="32" height="32" viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
          <defs>
            <linearGradient id="slg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a78bfa"/>
              <stop offset="100%" stopColor="#7c3aed"/>
            </linearGradient>
          </defs>
          <polygon points="36,2 70,36 36,70 2,36" fill="url(#slg)"/>
          <polygon points="36,12 60,36 36,60 12,36" fill="none" stroke="white" strokeWidth="1.5" opacity="0.2"/>
          <line x1="22" y1="24" x2="22" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="50" y1="24" x2="50" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="22" y1="24" x2="50" y2="48" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
        </svg>
        <div style={{minWidth:0,flex:1}}>
          <span className="sidebar-logo-name">Nexiora</span>
          {/* W439: il nome dell'azienda che usa Nexiora si deve notare (prima era grigio, 10px) */}
          {orgVisitata?.name && (
            <div className="sidebar-azienda" title={orgVisitata.name}>{orgVisitata.name}</div>
          )}
        </div>
      </div>

      {/* Banner piano */}
      {isTrial && giorniRimasti<=14 && (
        <div style={{margin:"6px 8px 0",padding:"7px 10px",background:"rgba(5,150,105,.15)",border:"1px solid rgba(5,150,105,.3)",borderRadius:8,fontSize:11,color:"#6ee7b7",display:"flex",alignItems:"center",gap:6}}>
          <span>🎁</span>
          <span><strong>{giorniRimasti} giorni</strong> di prova rimasti</span>
        </div>
      )}
      {isBase && (
        <div style={{margin:"6px 8px 0",padding:"7px 10px",background:"rgba(220,38,38,.1)",border:"1px solid rgba(220,38,38,.2)",borderRadius:8,fontSize:11,color:"#fca5a5",display:"flex",alignItems:"center",gap:6}}>
          <span>🔒</span>
          <span>Prova terminata — <strong>sola lettura</strong></span>
        </div>
      )}

      {/* Ricerca globale */}
      <div style={{ padding:"8px 8px 0" }}>
        <button onClick={()=>setShowGlobalSearch(true)}
          style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 10px",background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.1)",borderRadius:8,cursor:"pointer",color:"rgba(255,255,255,.5)",fontSize:12,fontFamily:"'Inter',sans-serif",textAlign:"left"}}>
          <Search size={13}/>
          <span style={{flex:1}}>Cerca...</span>
          <span style={{fontSize:10,background:"rgba(255,255,255,.1)",padding:"1px 5px",borderRadius:4}}>⌘K</span>
        </button>
      </div>
      {showGlobalSearch && <GlobalSearch onNavigate={onNavigate} onClose={()=>setShowGlobalSearch(false)}/>}

      {/* Home — voce fissa sempre visibile, indipendente dalla scheda selezionata.
          Non deve mai finire dentro un blocco (es. Gestione) soggetto a permessi per sezione,
          perché è la pagina di atterraggio di tutti, non solo di chi ha accesso a Gestione. */}
      <div style={{padding:"6px 8px 0"}}>
        <button className={`nav-item ${currentPage==="home"?"active":""}`}
          onClick={()=>onNavigate({page:"home"})}
          style={{width:"100%",background:currentPage==="home"?"rgba(124,58,237,.25)":"transparent",border:"none",borderRadius:8}}>
          <LayoutDashboard size={15}/>
          <span style={{flex:1,textAlign:"left",fontWeight:600,fontSize:12}}>Home</span>
          {/* Per chi è appena arrivato (prime 2 settimane dal primo ingresso): un segno che la
              Home è il punto da cui orientarsi. Poi sparisce da solo. */}
          {nuovoArrivato && <span style={{fontSize:9.5,fontWeight:700,color:"#fff",background:"#7c3aed",borderRadius:8,padding:"1px 7px",whiteSpace:"nowrap"}}>Inizia da qui</span>}
        </button>
      </div>

      {/* W444: Chat e Notifiche in alto, sotto Home, per tutti (deciso con Paolo il 30/10/2026):
          si usano ogni giorno e hanno il contatore. Prima un operaio senza la scheda Gestione non
          le aveva nel menu (le notifiche solo dalla Home, la chat mai). */}
      {(
        <div style={{padding:"2px 8px 0"}}>
          <NavItem id="chat" icon={MessageSquare} label="Chat" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
          <NavItem id="notifications" icon={Bell} label="Notifiche" count={notifCount||0} currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
        </div>
      )}

      {/* Nexiora Insight — sempre visibile sopra i tab */}
      {(isOrgAdmin||isPlatformOwner) && (
        <div style={{padding:"6px 8px 0"}}>
          <button className={`nav-item ${currentPage==="nexiora-insight"?"active":""}`}
            onClick={()=>onNavigate({page:"nexiora-insight"})}
            style={{width:"100%",background:currentPage==="nexiora-insight"?"rgba(124,58,237,.35)":"rgba(124,58,237,.14)",border:"1px solid rgba(167,139,250,.25)",borderRadius:8}}>
            <span style={{fontSize:15}}>🔮</span>
            <span style={{flex:1,textAlign:"left",fontWeight:700,fontSize:12}}>Nexiora Insight</span>
          </button>
        </div>
      )}

      {/* Tab switcher — Vista completa sopra, 4 tab sotto */}
      <div style={{padding:"8px 8px 0"}}>
        {/* Pulsante Vista completa — larghezza intera */}
        {(isPlatformOwner||isOrgAdmin||canSeeTab("tab-tutto")) && (
          <button onClick={()=>setSidebarTab("tutto")}
            style={{width:"100%",padding:"7px 10px",borderRadius:7,cursor:"pointer",
              fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:700,marginBottom:4,
              display:"flex",alignItems:"center",justifyContent:"center",gap:6,
              background:sidebarTab==="tutto"?"rgba(124,58,237,.4)":"rgba(255,255,255,.06)",
              border:sidebarTab==="tutto"?"1px solid rgba(165,180,252,.4)":"1px solid transparent",
              color:sidebarTab==="tutto"?"#e9d5ff":"rgba(255,255,255,.45)"}}>
            🔲 Vista completa
          </button>
        )}
        {/* 4 tab specifici */}
        <div style={{display:"flex",gap:2}}>
          {[
            {id:"progetti", label:"📁", title:"Progetti"},
            {id:"personale",label:"👤", title:"Personale"},
            {id:"acquisti", label:"🛒", title:"Acquisti"},
            {id:"gestione", label:"⚙️", title:"Gestione"},
          ].filter(t=>isPlatformOwner||isOrgAdmin||canSeeTab("tab-"+t.id)).map(t=>(
            <button key={t.id} onClick={()=>setSidebarTab(t.id)}
              title={t.title}
              style={{flex:1,padding:"5px 2px",border:"none",borderRadius:6,cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:13,
                background:sidebarTab===t.id?"rgba(255,255,255,.18)":"transparent",
                color:sidebarTab===t.id?"#fff":"rgba(255,255,255,.45)",
                display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
              <span>{t.label}</span>
              <span style={{fontSize:9,fontWeight:sidebarTab===t.id?700:400}}>{t.title}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="sidebar-scroll">

        {/* ══ TAB TUTTO ══ */}
        {sidebarTab==="tutto" && (<>
          {/* Preferiti */}
          {favoriteProjects.length > 0 && (
            <>
              <div style={{fontSize:10,fontWeight:700,color:"#c4b5fd",textTransform:"uppercase",letterSpacing:"0.08em",padding:"7px 12px 4px",display:"flex",alignItems:"center",gap:6,background:"rgba(124,58,237,0.15)",margin:"4px 4px 2px",borderRadius:6}}>
                <Star size={11} fill="#c4b5fd"/> Preferiti
              </div>
              {favoriteProjects.map(p => (
                <button key={"fav-"+p.id}
                  className={`nav-item ${currentProjectId === p.id ? "active" : ""}`}
                  onClick={() => onNavigate({ page:"project", projectId:p.id, project:p })}>
                  <div className="dot" style={{ background: getHealthDot(p) }}/>
                  <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"left" }}>{p.name}</span>
                  <Star size={11} fill="#f59e0b" color="#f59e0b" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>
                </button>
              ))}
            </>
          )}
          {/* Clienti e commesse */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px 4px",background:"rgba(124,58,237,0.15)",margin:"4px 4px 2px",borderRadius:6,cursor:"pointer"}}
            onClick={()=>setProjectsOpen(o=>!o)}>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <ChevronDown size={12} color="#c4b5fd" style={{transform:projectsOpen?"rotate(0deg)":"rotate(-90deg)",transition:"transform .2s"}}/>
              <span style={{fontSize:10,fontWeight:700,color:"#c4b5fd",textTransform:"uppercase",letterSpacing:"0.08em"}}>Progetti</span>
            </div>
            {(isOrgAdmin||effectiveOrgId) && (
              <button onClick={e=>{e.stopPropagation();onNavigate({page:"new-project"});}} style={{background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.4)",display:"flex",alignItems:"center"}} title="Nuovo progetto">
                <Plus size={14}/>
              </button>
            )}
          </div>
          {projectsOpen && (()=>{
            const padri = regularProjects.filter(p=>p.isParent);
            const figli = regularProjects.filter(p=>!p.isParent);
            const senzaPadre = figli.filter(p=>!p.parentId);
            if(padri.length===0) return regularProjects.map(p=>(
              <div key={"t-"+p.id} style={{display:"flex",alignItems:"center",gap:2}}>
                <button className={`nav-item ${currentProjectId===p.id?"active":""}`} style={{flex:1,minWidth:0}} onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                  <div className="dot" style={{background:p.color||"#7c3aed"}}/> <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{p.name}</span>
                </button>
                {/* W422: archiviate (i clienti non lo sono, quindi l'elenco è senza gruppi): ripristina ed elimina */}
                {showArchived && (
                  <span style={{display:"flex",alignItems:"center",gap:3,flexShrink:0,paddingRight:4}}>
                    <span onClick={()=>updateDoc(doc(db,"projects",p.id),{archived:false})}
                      title="Ripristina" style={{cursor:"pointer",fontSize:14,color:"rgba(110,231,183,0.8)",lineHeight:1}}>↩</span>
                    {canDelete && <Trash2 size={12} color="rgba(255,100,100,0.8)" onClick={()=>setDeleteTarget(p)} style={{cursor:"pointer"}} aria-label="Elimina definitivamente"/>}
                  </span>
                )}
              </div>
            ));
            return (<>
              {padri.map(padre=>{
                const figliFather=figli.filter(p=>p.parentId===padre.id);
                const isAperto=clientiAperti[padre.id]===true;
                const hasActive=figliFather.some(p=>p.id===currentProjectId);
                return (
                  <div key={padre.id}>
                    <div style={{display:"flex",alignItems:"center"}}>
                      <button onClick={()=>toggleCliente(padre.id)} style={{display:"flex",alignItems:"center",gap:6,flex:1,padding:"5px 6px 5px 8px",background:hasActive?"rgba(124,58,237,0.2)":"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.8)",fontSize:12,fontFamily:"'Inter',sans-serif",textAlign:"left",borderRadius:6,margin:"1px 0"}}>
                        {isAperto?<ChevronDown size={12} style={{flexShrink:0,opacity:.6}}/>:<ChevronRight size={12} style={{flexShrink:0,opacity:.6}}/>}
                        <span style={{flex:1,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12}}>{(padre.clienteNome||padre.name).toUpperCase()}</span>
                        <span style={{fontSize:10,opacity:.4,flexShrink:0,marginRight:4}}>{figliFather.length}</span>
                      </button>
                      <button onClick={()=>onNavigate({page:"new-project",parentId:padre.id,clienteNome:padre.clienteNome||padre.name})} style={{background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.3)",padding:"4px 6px",flexShrink:0,display:"flex",alignItems:"center"}} onMouseOver={e=>e.currentTarget.style.color="rgba(255,255,255,0.8)"} onMouseOut={e=>e.currentTarget.style.color="rgba(255,255,255,0.3)"}><Plus size={13}/></button>
                    </div>
                    {isAperto && figliFather.map(p=>(
                      <div key={"t-"+p.id} style={{display:"flex",alignItems:"center",gap:2}}>
                        <button className={`nav-item ${currentProjectId===p.id?"active":""}`} style={{paddingLeft:22,flex:1,minWidth:0}} onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                          <div className="dot" style={{background:getHealthDot(p),width:6,height:6,flexShrink:0,borderRadius:"50%"}}/>
                          <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left",fontSize:11}}>
                            {p.statoCommessa==="chiusa" && <span style={{marginRight:3,opacity:.7}} title="Commessa chiusa">🔒</span>}
                            {p.codiceCommessa ? <span style={{fontFamily:"monospace",color:"rgba(196,181,253,0.9)",marginRight:4}}>{p.codiceCommessa}</span> : null}
                            <span style={{opacity:p.statoCommessa==="chiusa"?0.55:1}}>{p.name}</span>
                          </span>
                          {taskCountMap[p.id] > 0 && (
                            <span style={{fontSize:9,fontWeight:800,background:"rgba(124,58,237,0.4)",color:"#e9d5ff",borderRadius:10,padding:"1px 5px",flexShrink:0}}>
                              {taskCountMap[p.id]}
                            </span>
                          )}
                          {!showArchived && <Star size={10} fill="none" color="rgba(255,255,255,0.2)" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>}
                        </button>
                        {showArchived && (
                          <span style={{display:"flex",alignItems:"center",gap:3,flexShrink:0,paddingRight:4}}>
                            <span onClick={()=>updateDoc(doc(db,"projects",p.id),{archived:false})}
                              title="Ripristina" style={{cursor:"pointer",fontSize:14,color:"rgba(110,231,183,0.8)",lineHeight:1}}>↩</span>
                            {canDelete && <Trash2 size={12} color="rgba(255,100,100,0.8)" onClick={()=>setDeleteTarget(p)} style={{cursor:"pointer"}}/>}
                          </span>
                        )}
                      </div>
                    ))}
                    {isAperto&&figliFather.length===0&&<div style={{paddingLeft:24,fontSize:11,color:"rgba(255,255,255,.25)",padding:"3px 10px 3px 24px"}}>Nessuna commessa</div>}
                  </div>
                );
              })}
              {senzaPadre.map(p=>(
                <div key={"t-"+p.id} style={{display:"flex",alignItems:"center",gap:2}}>
                  <button className={`nav-item ${currentProjectId===p.id?"active":""}`} style={{flex:1,minWidth:0}} onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                    <div className="dot" style={{background:p.color||"#7c3aed"}}/>
                    <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{p.name}</span>
                    {!showArchived && <Star size={10} fill="none" color="rgba(255,255,255,0.2)" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>}
                  </button>
                  {showArchived && (
                    <span style={{display:"flex",alignItems:"center",gap:3,flexShrink:0,paddingRight:4}}>
                      <span onClick={()=>updateDoc(doc(db,"projects",p.id),{archived:false})}
                        title="Ripristina" style={{cursor:"pointer",fontSize:14,color:"rgba(110,231,183,0.8)",lineHeight:1}}>↩</span>
                      {canDelete && <Trash2 size={12} color="rgba(255,100,100,0.8)" onClick={()=>setDeleteTarget(p)} style={{cursor:"pointer"}}/>}
                    </span>
                  )}
                </div>
              ))}
            </>);
          })()}
          {/* W422: anche nella Vista completa si arriva alle commesse archiviate (ripristino ed eliminazione) */}
          {projectsOpen && (
            <button onClick={()=>setShowArchived(s=>!s)}
              style={{display:"flex",alignItems:"center",gap:6,width:"100%",padding:"6px 10px",background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
              <Archive size={11}/> {showArchived?"Nascondi archiviati":"Mostra archiviati"}
            </button>
          )}
          {/* Sezioni funzionali */}
          <SidebarGroup id="tutto-personale" pagine={["my-tasks","approvazione-cartellini","approvazione-ferie-permessi","contratti-manutenzione","dipendenti","elaborazione-offerte","le-mie-commesse","mio-calendario","registro-note","le-mie-bozze","le-mie-ore","nota-spese","nuova-richiesta-potenziale","sopralluoghi-senza-commessa","ordini-materiali","pianificazione-team","presenze","programmazione","report-ore","richiedi-nf","richiesta-ferie","segnalazioni-cliente","strumenti-tecnici"]} currentPage={currentPage} label="Personale" defaultOpen={false} viola={true}>
            <NavItem id="mio-calendario" icon={CalendarDays} label="Il mio calendario" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="my-tasks" icon={CheckSquare} label="Le mie attività" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="registro-note" icon={FileText} label="Registro delle mie note" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="le-mie-bozze" icon={Archive} label="Le mie bozze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {vediLeMieOre && <NavItem id="le-mie-ore" icon={Timer} label="Le mie ore" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>}
            <NavItem id="presenze" icon={Clock} label="Presenze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("programmazione") && (<NavItem id="programmazione" icon={CalendarDays} label="Programmazione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("pianificazione-team") && (<NavItem id="pianificazione-team" icon={BarChart2} label="Capacità Produttiva" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("elaborazione-offerte") && (<NavItem id="elaborazione-offerte" icon={Calculator} label="Elaborazione e Emissione Offerte" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("report-ore") && (<NavItem id="report-ore" icon={BarChart2} label="Report Ore" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("approvazione-cartellini") && (<NavItem id="approvazione-cartellini" icon={CheckSquare} label="Approvazione Cartellini" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("dipendenti") && (<NavItem id="dipendenti" icon={UserCheck} label="Dipendenti" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="nota-spese" icon={Receipt} label="Nota Spese" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="richiesta-ferie" icon={CalendarDays} label="Ferie e Permessi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("le-mie-commesse") && (<NavItem id="le-mie-commesse" icon={BarChart2} label="Le mie commesse" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="ordini-materiali" icon={Package} label="Ordini materiali al Magazzino" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="richiedi-nf" icon={FileText} label="Richiedi NF" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="segnalazioni-cliente" icon={Megaphone} label="Segnalazioni Cliente" count={segMieAperteCount||0} currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="nuova-richiesta-potenziale" icon={FileText} label="Richieste commerciali per potenziale cliente" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("sopralluoghi-senza-commessa") && (<NavItem id="sopralluoghi-senza-commessa" icon={FileText} label="Sopralluoghi senza commessa" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("contratti-manutenzione") && (<NavItem id="contratti-manutenzione" icon={Wrench} label="Contratti di Manutenzione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="strumenti-tecnici" icon={Wrench} label="Strumenti Tecnici" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
          </SidebarGroup>
          <SidebarGroup id="tutto-acquisti" pagine={["centro-acquisti","listino-materiali","magazzino","rientro-materiali","scadenziario"]} currentPage={currentPage} label="Acquisti" defaultOpen={false} viola={true}>
            {canSee("centro-acquisti") && (<NavItem id="centro-acquisti" icon={ShoppingCart} label="NF – ODA – Fornitori" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("magazzino") && (<NavItem id="magazzino" icon={Package} label="Evasione richieste al magazzino" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("scadenziario") && (<NavItem id="scadenziario" icon={Clock} label="Scadenzario" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("rientro-materiali") && (<NavItem id="rientro-materiali" icon={RotateCcw} label="Rientro Materiali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {(isOrgAdmin||isPlatformOwner) && (<NavItem id="listino-materiali" icon={Tags} label="Listino Materiali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
          </SidebarGroup>
          <SidebarGroup id="direzione" pagine={["control-center","cruscotto-scadenze"]} currentPage={currentPage} label="Direzione" defaultOpen={false} viola={true}>
            {canSee("control-center") && (<NavItem id="control-center" icon={BarChart2} label="Centro Operativo" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("cruscotto-scadenze") && (<NavItem id="cruscotto-scadenze" icon={Clock} label="Cruscotto Scadenze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
          </SidebarGroup>
          {gruppiAmministrazione}
        </>)}

        {/* CHECKLIST ONBOARDING */}

        {/* ══ TAB PROGETTI ══ */}
        {sidebarTab==="progetti" && (<>
          {/* Preferiti */}
          {favoriteProjects.length > 0 && (
            <>
              <div style={{fontSize:10,fontWeight:700,color:"#c4b5fd",textTransform:"uppercase",letterSpacing:"0.08em",padding:"7px 12px 4px",display:"flex",alignItems:"center",gap:6,background:"rgba(124,58,237,0.15)",margin:"4px 4px 2px",borderRadius:6}}>
                <Star size={11} fill="#c4b5fd"/> Preferiti
              </div>
              {favoriteProjects.map(p => (
                <button key={"fav-"+p.id}
                  className={`nav-item ${currentProjectId === p.id ? "active" : ""}`}
                  onClick={() => onNavigate({ page:"project", projectId:p.id, project:p })}>
                  <div className="dot" style={{ background: getHealthDot(p) }}/>
                  <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"left" }}>{p.name}</span>
                  <Star size={11} fill="#f59e0b" color="#f59e0b" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>
                </button>
              ))}
            </>
          )}

          {/* Progetti raggruppati per cliente */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px 4px",background:"rgba(124,58,237,0.15)",margin:"4px 4px 2px",borderRadius:6,cursor:"pointer"}}
            onClick={() => setProjectsOpen(o => !o)}>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <ChevronDown size={12} color="#c4b5fd" style={{transform: projectsOpen?"rotate(0deg)":"rotate(-90deg)",transition:"transform .2s"}}/>
              <span style={{fontSize:10,fontWeight:700,color:"#c4b5fd",textTransform:"uppercase",letterSpacing:"0.08em"}}>Progetti</span>
            </div>
            {(isOrgAdmin || effectiveOrgId) && (
              <button onClick={e => { e.stopPropagation(); onNavigate({ page:"new-project" }); }}
                style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.4)", display:"flex", alignItems:"center" }}
                title="Nuovo progetto">
                <Plus size={14}/>
              </button>
            )}
          </div>
          <button onClick={() => setSoloMieCommesse(v=>!v)}
            style={{display:"flex",alignItems:"center",gap:5,margin:"0 8px 4px",padding:"4px 8px",
              border:"1px solid "+(soloMieCommesse?"#a78bfa":"transparent"),borderRadius:6,
              background:soloMieCommesse?"rgba(124,58,237,0.25)":"transparent",
              color:soloMieCommesse?"#e9d5ff":"rgba(255,255,255,0.4)",fontSize:10,fontWeight:600,cursor:"pointer",width:"calc(100% - 16px)"}}>
            👤 Solo le mie commesse
          </button>
          {projectsOpen && (() => {
            const padri = regularProjects.filter(p=>p.isParent);
            const figli = regularProjects.filter(p=>!p.isParent);
            const senzaPadre = figli.filter(p=>!p.parentId);

            if(padri.length===0) return regularProjects.map(p=>(
              <div key={"proj-"+p.id} style={{display:"flex",alignItems:"center",gap:2}}>
                <button
                  className={`nav-item ${currentProjectId===p.id?"active":""}`}
                  style={{flex:1,minWidth:0}}
                  onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                  <div className="dot" style={{background:p.color||"#7c3aed"}}/>
                  <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{p.statoCommessa==="chiusa"&&<span style={{marginRight:3}} title="Chiusa">🔒</span>}{p.codiceCommessa && <span style={{fontFamily:"monospace",color:"rgba(196,181,253,0.9)",marginRight:4,fontSize:11}}>{p.codiceCommessa}</span>}<span style={{opacity:p.statoCommessa==="chiusa"?0.55:1}}>{p.name}</span></span>
                  {!showArchived && <Star size={11} fill="none" color="rgba(255,255,255,0.25)" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>}
                </button>
                {showArchived && (
                  <span style={{display:"flex",alignItems:"center",gap:3,flexShrink:0,paddingRight:4}}>
                    <span onClick={()=>updateDoc(doc(db,"projects",p.id),{archived:false})}
                      title="Ripristina" style={{cursor:"pointer",fontSize:14,color:"rgba(110,231,183,0.8)",lineHeight:1}}>↩</span>
                    {canDelete && <Trash2 size={12} color="rgba(255,100,100,0.8)" onClick={()=>setDeleteTarget(p)} style={{cursor:"pointer"}}/>}
                  </span>
                )}
              </div>
            ));

            return (<>
              {padri.map(padre => {
                const figliFather = figli.filter(p=>p.parentId===padre.id);
                const isAperto = clientiAperti[padre.id] === true;
                const hasActive = figliFather.some(p=>p.id===currentProjectId);
                return (
                  <div key={padre.id}>
                    <div style={{display:"flex",alignItems:"center",gap:0}}>
                      <button onClick={()=>toggleCliente(padre.id)}
                        style={{display:"flex",alignItems:"center",gap:6,flex:1,padding:"5px 6px 5px 8px",background:hasActive?"rgba(124,58,237,0.2)":"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.8)",fontSize:12,fontFamily:"'Inter',sans-serif",textAlign:"left",borderRadius:6,margin:"1px 0"}}>
                        {isAperto ? <ChevronDown size={12} style={{flexShrink:0,opacity:.6}}/> : <ChevronRight size={12} style={{flexShrink:0,opacity:.6}}/>}
                        <span style={{flex:1,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",letterSpacing:".01em",fontSize:12}}>
                          {(padre.clienteNome||padre.name).toUpperCase()}
                        </span>
                        <span style={{fontSize:10,opacity:.4,flexShrink:0,marginRight:4}}>{figliFather.length}</span>
                      </button>
                      <button onClick={()=>onNavigate({page:"new-project",parentId:padre.id,clienteNome:padre.clienteNome||padre.name})}
                        title="Nuova commessa"
                        style={{background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.3)",padding:"4px 6px",flexShrink:0,display:"flex",alignItems:"center"}}
                        onMouseOver={e=>e.currentTarget.style.color="rgba(255,255,255,0.8)"}
                        onMouseOut={e=>e.currentTarget.style.color="rgba(255,255,255,0.3)"}>
                        <Plus size={13}/>
                      </button>
                    </div>
                    {isAperto && figliFather.map(p=>(
                      <button key={"proj-"+p.id}
                        className={`nav-item ${currentProjectId===p.id?"active":""}`}
                        style={{paddingLeft:22}}
                        onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                        <div className="dot" style={{background:getHealthDot(p),width:6,height:6,flexShrink:0,borderRadius:"50%"}}/>
                        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left",fontSize:11}}>{p.statoCommessa==="chiusa"&&<span style={{marginRight:3}} title="Chiusa">🔒</span>}{p.codiceCommessa && <span style={{fontFamily:"monospace",color:"rgba(196,181,253,0.9)",marginRight:4}}>{p.codiceCommessa}</span>}<span style={{opacity:p.statoCommessa==="chiusa"?0.55:1}}>{p.name}</span></span>
                        {showArchived ? (
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span onClick={e=>{e.stopPropagation(); updateDoc(doc(db,"projects",p.id),{archived:false});}}
                              title="Ripristina" style={{cursor:"pointer",fontSize:13,color:"rgba(110,231,183,0.7)"}}>↩</span>
                            {canDelete && (
                              <Trash2 size={11} color="rgba(255,100,100,0.5)"
                                onClick={e=>{e.stopPropagation();setDeleteTarget(p);}}
                                style={{cursor:"pointer"}}/>
                            )}
                          </span>
                        ) : (
                          <Star size={10} fill="none" color="rgba(255,255,255,0.2)" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>
                        )}
                      </button>
                    ))}
                    {isAperto && figliFather.length===0 && (
                      <div style={{paddingLeft:24,fontSize:11,color:"rgba(255,255,255,.25)",padding:"3px 10px 3px 24px"}}>Nessuna commessa</div>
                    )}
                  </div>
                );
              })}
              {senzaPadre.map(p=>(
                <div key={"proj-"+p.id} style={{display:"flex",alignItems:"center",gap:2}}>
                  <button
                    className={`nav-item ${currentProjectId===p.id?"active":""}`}
                    style={{flex:1,minWidth:0}}
                    onClick={()=>onNavigate({page:"project",projectId:p.id,project:p})}>
                    <div className="dot" style={{background:p.color||"#7c3aed"}}/>
                    <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{p.name}</span>
                    {!showArchived && <Star size={10} fill="none" color="rgba(255,255,255,0.2)" onClick={e=>toggleFavorite(e,p)} style={{flexShrink:0,cursor:"pointer"}}/>}
                  </button>
                  {showArchived && (
                    <span style={{display:"flex",alignItems:"center",gap:3,flexShrink:0,paddingRight:4}}>
                      <span onClick={()=>updateDoc(doc(db,"projects",p.id),{archived:false})}
                        title="Ripristina" style={{cursor:"pointer",fontSize:14,color:"rgba(110,231,183,0.8)",lineHeight:1}}>↩</span>
                      {canDelete && <Trash2 size={12} color="rgba(255,100,100,0.8)" onClick={()=>setDeleteTarget(p)} style={{cursor:"pointer"}}/>}
                    </span>
                  )}
                </div>
              ))}
            </>);
          })()}

            <button onClick={()=>setShowArchived(s=>!s)}
              style={{display:"flex",alignItems:"center",gap:6,width:"100%",padding:"6px 10px",background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.25)",fontSize:11,fontFamily:"'Inter',sans-serif",marginTop:4}}>
              <Archive size={11}/> {showArchived?"Nascondi archiviati":"Mostra archiviati"}
            </button>
        </>)}

        {/* ══ TAB PERSONALE ══ */}
        {sidebarTab==="personale" && (<>
          <SidebarGroup id="operai-grp" pagine={["contratti-manutenzione","da-fatturare","le-mie-commesse","mio-calendario","registro-note","le-mie-bozze","le-mie-ore","my-tasks","nota-spese","nuova-richiesta-potenziale","sopralluoghi-senza-commessa","ordini-materiali","presenze","programmazione","richiedi-nf","richiesta-ferie","segnalazioni-cliente","strumenti-tecnici"]} currentPage={currentPage} label="👤 Area Personale" defaultOpen={true} viola={true}>
            <NavItem id="mio-calendario" icon={CalendarDays} label="Il mio calendario" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="registro-note" icon={FileText} label="Registro delle mie note" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="le-mie-bozze" icon={Archive} label="Le mie bozze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {vediLeMieOre && <NavItem id="le-mie-ore" icon={Timer} label="Le mie ore" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>}
            <NavItem id="presenze" icon={Clock} label="Presenze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("programmazione") && (<NavItem id="programmazione" icon={CalendarDays} label="Programmazione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="nota-spese" icon={Receipt} label="Nota Spese" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="richiesta-ferie" icon={CalendarDays} label="Ferie e Permessi" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="my-tasks" icon={CheckSquare} label="Le mie attività" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("le-mie-commesse") && (<NavItem id="le-mie-commesse" icon={BarChart2} label="Le mie commesse" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {isPm && (<NavItem id="da-fatturare" icon={FileText} label="Richieste di fatturazione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="ordini-materiali" icon={Package} label="Ordini materiali al Magazzino" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="richiedi-nf" icon={FileText} label="Richiedi NF" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="segnalazioni-cliente" icon={Megaphone} label="Segnalazioni Cliente" count={segMieAperteCount||0} currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            <NavItem id="nuova-richiesta-potenziale" icon={FileText} label="Richieste commerciali per potenziale cliente" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
            {canSee("sopralluoghi-senza-commessa") && (<NavItem id="sopralluoghi-senza-commessa" icon={FileText} label="Sopralluoghi senza commessa" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("contratti-manutenzione") && (<NavItem id="contratti-manutenzione" icon={Wrench} label="Contratti di Manutenzione" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            <NavItem id="strumenti-tecnici" icon={Wrench} label="Strumenti Tecnici" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>
          </SidebarGroup>
        </>)}

        {/* ══ TAB ACQUISTI ══ */}
        {sidebarTab==="acquisti" && (<>
          <SidebarGroup id="acquisti-grp" pagine={["centro-acquisti","listino-materiali","magazzino","rientro-materiali","scadenziario"]} currentPage={currentPage} label="🛒 Area Acquisti" defaultOpen={true} viola={true}>
            {canSee("centro-acquisti") && (<NavItem id="centro-acquisti" icon={ShoppingCart} label="NF – ODA – Fornitori" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("magazzino") && (<NavItem id="magazzino" icon={Package} label="Evasione richieste al magazzino" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("scadenziario") && (<NavItem id="scadenziario" icon={Clock} label="Scadenzario" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("rientro-materiali") && (<NavItem id="rientro-materiali" icon={RotateCcw} label="Rientro Materiali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {(isOrgAdmin||isPlatformOwner) && (<NavItem id="listino-materiali" icon={Tags} label="Listino Materiali" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
          </SidebarGroup>
        </>)}

        {/* ══ TAB GESTIONE ══ */}
        {sidebarTab==="gestione" && (<>
          <SidebarGroup id="gestione-op" pagine={["centro-evoluto","control-center","cruscotto-scadenze","elaborazione-offerte","pianificazione-team","report-ore"]} currentPage={currentPage} label="Direzione" defaultOpen={true} viola={true}>
            {canSee("control-center") && (<NavItem id="control-center" icon={BarChart2} label="Centro Operativo" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("cruscotto-scadenze") && (<NavItem id="cruscotto-scadenze" icon={Clock} label="Cruscotto Scadenze" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("report-ore") && (<NavItem id="report-ore" icon={BarChart2} label="Report Ore" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("pianificazione-team") && (<NavItem id="pianificazione-team" icon={BarChart2} label="Capacità Produttiva" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
            {canSee("elaborazione-offerte") && (<NavItem id="elaborazione-offerte" icon={Calculator} label="Elaborazione e Emissione Offerte" currentPage={currentPage} currentProjectId={currentProjectId} onNavigate={onNavigate}/>)}
          </SidebarGroup>
          {gruppiAmministrazione}
        </>)}

      </div>{/* fine sidebar-scroll */}

      {/* Footer */}
      <div className="sidebar-footer">
        {onBackToOwner && (
          <button onClick={onBackToOwner}
            style={{ width:"100%", padding:"8px", background:"rgba(124,58,237,0.2)", border:"1px solid rgba(124,58,237,0.4)", borderRadius:8, color:"#a78bfa", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"'Inter',sans-serif", fontWeight:600, marginBottom:8 }}>
            ← Torna a Nexiora Owner
          </button>
        )}
        {/* Banner promemoria approvazione — solo giorni 1-3 del mese */}
        {(()=>{
          const oggi = new Date();
          const giorno = oggi.getDate();
          const puoApprovare = isOrgAdmin || isPlatformOwner ||
            canSee("approvazione-cartellini") || canSee("approvazione-note-spese");
          if(giorno > 3 || !puoApprovare) return null;
          return (
            <div style={{ margin:"0 0 10px", padding:"10px 12px", background:"#fffbeb",
              border:"1px solid #fde68a", borderLeft:"3px solid #f59e0b", borderRadius:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#92400e", marginBottom:2 }}>
                ⏰ Inizio mese
              </div>
              <div style={{ fontSize:11, color:"#92400e", lineHeight:1.4 }}>
                Ricorda di approvare i cartellini presenze e le note spese del mese scorso.
              </div>
            </div>
          );
        })()}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
          <div className="avatar" style={{ width:28, height:28, fontSize:12 }}>
            {userProfile?.displayName?.[0]?.toUpperCase() || "U"}
          </div>
          <div style={{ flex:1, overflow:"hidden" }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
              {userProfile?.displayName}
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>
              {isOrgAdmin ? "Admin" : "Membro"}
            </div>
          </div>
          <button className="btn-icon" onClick={() => onNavigate({ page:"settings" })}
            style={{ color:"rgba(255,255,255,0.4)" }} title="Impostazioni">
            <Settings size={15}/>
          </button>
        </div>
        <button onClick={logout}
          style={{ width:"100%", padding:"6px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:6, color:"rgba(255,255,255,0.6)", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"'Inter',sans-serif" }}>
          <LogOut size={13}/> Esci
        </button>
        <div style={{ display:"flex", gap:6, marginTop:6 }}>
          <button onClick={()=>window.dispatchEvent(new CustomEvent("nexiora:guida"))}
            style={{ flex:1, minWidth:0, padding:"8px", background:"rgba(124,58,237,0.2)", border:"1px solid rgba(124,58,237,0.4)", borderRadius:8, color:"#c4b5fd", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"'Inter',sans-serif" }}>
            📖 Guida / Come iniziare
          </button>
          {/* Come si fa: la scheda del Centro assistenza per la pagina aperta, sul sito, in una nuova scheda */}
          <a href={urlGuida(currentPage, schedaCommessa)} target="_blank" rel="noopener noreferrer"
            title="Come si fa: apre la guida passo passo di questa pagina in una nuova scheda del browser"
            aria-label="Guida passo passo di questa pagina (si apre in una nuova scheda)"
            style={{ flex:"0 0 34px", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:8, background:"rgba(124,58,237,0.2)", border:"1px solid rgba(124,58,237,0.4)", color:"#c4b5fd", fontSize:14, fontWeight:800, textDecoration:"none", fontFamily:"'Inter',sans-serif" }}>
            ?
          </a>
        </div>
      </div>

      {deleteTarget && (
        <DeleteProjectModal project={deleteTarget} onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); if (currentProjectId === deleteTarget.id) onNavigate({ page:"home" }); }}/>
      )}
    </div>
    </PianoNavContext.Provider>
  );
}
